/**
 * Product Form Components
 *
 */
class ProductForm extends HTMLElement {
  constructor() {
    super();

    this._isSubmitting = false;
    this.form = Utility.DomSelector.$('form', this);
    // Prefer cart-form inside cart drawer when present (product/collection pages); fallback to template-cart (cart page)
    const cartDrawer = document.getElementById('component-cart-drawer');
    this.cartElement = cartDrawer?.querySelector('cart-form') || Utility.DomSelector.$('cart-form');
    this.cartSectionId = this.cartElement?.dataset?.sectionId || 'template-cart';
    this.submitButton = Utility.DomSelector.$('[type="submit"]', this);
    this.qtyInput = Utility.DomSelector.$('[data-qty-input]', this);
    this.productId = this.dataset.productid;

    this.boundOnSubmitHandler = this.onSubmitHandler.bind(this);
  }

  connectedCallback() {
    this.form?.addEventListener('submit', this.boundOnSubmitHandler);
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.boundOnSubmitHandler);
  }

  updateProductDetails(container, variant, html) {
    if (!container || !variant) {
      this.toggleAddButton('disable');
      this.updateUnavailableMessage(container, variant);
      return;
    }

    this.price = Utility.DomSelector.$('[data-current-price]', container);
    this.comparePrice = Utility.DomSelector.$('[data-compare-price]', container);
    this.discountPercentage = Utility.DomSelector.$('[data-discount-percentage]', container);
    this.featuredImage = Utility.DomSelector.$('[data-featured-image]', container);
    this.productSlider = Utility.DomSelector.$('[data-product-slider]', container);

    this.toggleAddButton('enable', variant.available, variant.title);
    this.updateUnavailableMessage(container, variant);
    this.updateURLandID(variant);
    this.updateDynamicSections(container, html);
    this.priceUpdate(variant);
    this.updateMedia(variant.featured_media);
  }

   /**
   * Render Price details based on current variant
   */
   priceUpdate(variant) {
    if (!this.price) return;

    this.price.textContent = Utility.formatMoney(variant.price, globalThis.themeData.money_format);
    this.price.classList.toggle('discounted-price', variant.compare_at_price > variant.price);

    if(this.comparePrice) {
      this.comparePrice.innerHTML = `<del class="money">${Utility.formatMoney(variant.compare_at_price, globalThis.themeData.money_format)}</del>`;
      this.comparePrice.classList.toggle('d-none', !variant.compare_at_price || variant.compare_at_price <= 0 || variant.compare_at_price === variant.price);
    }
    
    if(this.discountPercentage){
      const discountPercentage = variant.compare_at_price - variant.price;
      const discountPercentageValue = Math.round((discountPercentage / variant.compare_at_price) * 100);
      this.discountPercentage.textContent = `(-${discountPercentageValue}%)`;
      this.discountPercentage.classList.toggle('d-none', variant.compare_at_price <= variant.price);
    }
  }

  /**
   * Updates dynamic variant-dependent sections (SKU, inventory, quantity, volume pricing)
   * Scoped to the given container so it works for both PDP and product cards
   * The HTML for the product card comes from sections/product-card-rendering.liquid while the Product page uses sections/template-product.liquid.
   * @param {Element} container
   * @param {Document} html - Parsed section HTML from fetchProductHTML
   */
  updateDynamicSections(container, html) {
    if (!container || !html) return;

    Utility.DOM.updateSelectors([
      `[data-variant-inventory]`,
      `[data-variant-sku]`,
      `[data-product-quantity-picker]`,
      `[data-variant-volumepricing]`
      /*,`[data-compare-price]`,
      `[data-current-price]`,
      `[data-discount-percentage]`*/
    ], html, container);
  }

  /**
   * Toggle unavailable message visibility based on variant availability
   * @param {Element} container
   * @param {Object|Boolean} variant - Shopify variant object or false when invalid
   */
  updateUnavailableMessage(container, variant) {
    const unavailableMessage = Utility.DomSelector.$('[data-product-unavailable-message]', container);
    if (!unavailableMessage) return;

    const showMessage = !variant || !variant.available;
    unavailableMessage.classList.toggle('d-none', !showMessage);
  }

  /**
   * Toggle the button based on product availability ( add to cart / soldOut )
   * @param {Text} status enable / disable
   * @param {Boolean} variantAvailable true/false
   */
  toggleAddButton(status, variantAvailable, variantTitle) {
    if (!this.submitButton) return;
    const isProductCard = this.dataset.format === 'product-card';
    const isUpsell = this.form?.classList.contains('cart-upsell-form');
    const addText = Utility.DomSelector.$('.add-text', this.submitButton);
    
    if (status === 'disable' || (!variantAvailable)) {
        if (isProductCard) {
          this.submitButton.classList.add('d-none');
          return;
        }

        this.submitButton.setAttribute('disabled', true);
        if(addText) {
          const text = status === 'disable' ? globalThis.variantStrings.unavailable : globalThis.variantStrings.soldOut;
          addText.textContent = text;
          this.submitButton.setAttribute('aria-label', `${text}: ${variantTitle}`);
        }
    } else {
        if (isProductCard) {
          this.submitButton.classList.remove('d-none');
        }

        this.submitButton.removeAttribute('disabled');
        if(addText) {
          const text = isUpsell ? globalThis.variantStrings.upsellAddText : globalThis.variantStrings.addToCart;
          addText.textContent = text;
          this.submitButton.setAttribute('aria-label', `${text}: ${variantTitle}`);
        }
    }
  }

  /**
   * Update URL on variant change event
   * @param {JSON} currentVariant 
   */
  updateURLandID(currentVariant) {
    if (!currentVariant || !this.form) return;

    if (this.dataset.format === 'product-page') {
      Utility.URL.replaceUrl(`variant=${currentVariant.id}`, {
        path: this.form.dataset.url
      });
    }
    Utility.DomSelector.$('input[name="id"]', this.form).value = currentVariant.id;
  }

  /**
   * Render selected variant image/slide
   * @param {object} variantMedia
   */
  updateMedia(variantMedia) {
    if (!variantMedia) return;

    if(this.featuredImage && variantMedia.preview_image?.src){
      this.featuredImage.src = `${variantMedia.preview_image.src}&width=850`;
      this.featuredImage.removeAttribute('srcset');
      Utility.CSS.remove(this.featuredImage, 'lozad');
    }else if(this.productSlider){
      const imageSlide = Utility.DomSelector.$(`[data-mediaID="${variantMedia.id}"]`, this.productSlider);
      if(imageSlide){
        const slideIndex = Array.from(imageSlide.parentNode.children).indexOf(imageSlide);
        if (this.productSlider?.swiper) this.productSlider.swiper.slideTo(slideIndex, 0, false);
      }
    }
  }

  /**
   * Product Form Submit event
   *
   * @param {evt} Event instance
   */
  async onSubmitHandler(evt) {
    evt.preventDefault();

    const submitButton = evt.submitter || this.submitButton;
    if (this._isSubmitting) return;

    this._isSubmitting = true;
    Utility.Animate.setLoading(submitButton, true, { disable: true });

    try {
      const res = await this.addToCartRequest();

      if (this.shouldRedirectToCheckout()) {
        window.location.assign('/checkout');
        return;
      }

      this.handleSubmitResponse(res);
    } catch (error) {
      const message = Utility.extractErrorMessage(error) || 'Failed to add to cart';
      Utility.handleError(message, 'Add to Cart');
      Utility.announceToScreenReader(message, 'assertive');
    } finally {
      this._isSubmitting = false;
      Utility.Animate.setLoading(submitButton, false);
    }
  }

  /**
   * Fast cart/add request — one round-trip with cart drawer section HTML.
   * Uses native fetch to avoid extra Utility.Fetch error-handling overhead.
   * @returns {Promise<object>}
   */
  async addToCartRequest() {
    const formData = new FormData(this.form);
    formData.append('sections', `${this.cartSectionId}`);

    const addUrl = `${globalThis.routes?.cart_add_url || '/cart/add'}.js`;
    const response = await fetch(addUrl, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: formData
    });

    const data = await response.json();

    // Error payloads include status (e.g. 422); success returns the line item with id/key
    if (!response.ok || data.status) {
      const error = new Error(data.description || data.message || 'Failed to add to cart');
      error.status = response.status || data.status;
      error.data = data;
      throw error;
    }

    return data;
  }

  /**
   * Handles API response from cart submission
   * @param {Object|String} response - API response (JSON object or string)
   */
  handleSubmitResponse(response) {
    const res = typeof response === 'object' ? response : JSON.parse(response);
    const cartHTML = res.sections?.[this.cartSectionId];
    const parsedCartHTML = cartHTML ? Utility.DOM.parse(cartHTML) : null;

    if (parsedCartHTML) {
      eventBus.publish(THEME_EVENTS.cartUpdate, {
        data: {
          action: 'add-item',
          item: res,
          section: parsedCartHTML,
          elementToFocus: this.submitButton
        }
      });
    } else if (this.cartElement?.initCartDrawer) {
      this.cartElement.initCartDrawer(this.submitButton);
    }

    if (this.qtyInput) this.qtyInput.value = this.qtyInput.min || 1;

    if (!this.cartElement) {
      if (parsedCartHTML) {
        const cartJSONEle = Utility.DOM.extract(parsedCartHTML, '[data-cartScriptJSON]');
        if (cartJSONEle) {
          globalThis.themeData.cart = { ...JSON.parse(cartJSONEle.textContent) };
        }
      }
      Utility.Cart.updateHeaderCount();
      Utility.handleSuccess(res.title, 'Added to Cart');
    }

    const itemCount = globalThis.themeData?.cart?.item_count;
    const countText = itemCount != null
      ? ` Cart now has ${itemCount} item${itemCount !== 1 ? 's' : ''}.`
      : '';
    Utility.announceToScreenReader(`${res.title} added to cart.${countText}`, 'assertive');
  }

  shouldRedirectToCheckout() {
    if (!this.submitButton) return false;

    const isEnabled = this.submitButton.dataset.directCheckoutEnabled === 'true';
    const keyword = this.submitButton.dataset.directCheckoutKeyword?.trim().toLowerCase();
    if (!isEnabled || !keyword) return false;

    const selectedVariant = this.currentVariant || globalThis.themeData?.product?.currentVariant;
    if (selectedVariant) {
      const variantSearchableText = [
        selectedVariant.title,
        ...(Array.isArray(selectedVariant.options) ? selectedVariant.options : [])
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return variantSearchableText.includes(keyword);
    }

    const selectedPlanInput = this.form?.querySelector('input[type="radio"]:checked[data-variant-title]');
    const fallbackTitle = (selectedPlanInput?.dataset?.variantTitle || this.submitButton.dataset.selectedVariantTitle || '').toLowerCase();
    return fallbackTitle.includes(keyword);
  }
}

customElements.define('product-form', ProductForm);
